import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, FadeUp, MaskedLine } from "@/components/motion";

const SUITS = [
  {
    suit: "♠",
    title: "Play Freely",
    body: "Be yourself. Flirt. Talk. Watch. Join in. Sit something out. Nobody owes anyone attention, attraction or participation.",
  },
  {
    suit: "♥",
    title: "Play Hard",
    body: "Adult play can be exciting, joyful, ridiculous, intimate and a lot of fun. Enthusiasm is welcome. Entitlement isn't.",
  },
  {
    suit: "♦",
    title: "Play Safe",
    body: "Ask. Listen. Pay attention. A yes can become a no at any time, and a no never requires an explanation.",
  },
  {
    suit: "♣",
    title: "Play Again",
    body: "Come back. Learn names. Introduce a mate. Recognise familiar faces. The best nights happen when strangers gradually stop being strangers.",
  },
];

const ThePlayers = () => (
  <div data-testid="the-players-page">
    <section className="lamp-glow mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 sm:pt-44">
      <Eyebrow>The Players</Eyebrow>
      <h1 className="mt-4 font-serif text-5xl leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl">
        <MaskedLine delay={0.1}>A carefully</MaskedLine>
        <MaskedLine delay={0.25} className="italic text-brass">
          dealt hand.
        </MaskedLine>
      </h1>
      <FadeUp delay={0.4}>
        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-ivory/70">
          Players Lounge isn't curated around looks. We don't have a preferred age, body type or
          gay stereotype.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
      <FadeUp>
        <div className="relative overflow-hidden rounded-2xl border border-brass/15">
          <img
            src="/images/players-community.png"
            alt="A diverse group of men in easy conversation around a lounge table"
            loading="lazy"
            className="aspect-[4/3] w-full object-cover sm:aspect-[16/9]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          <p className="absolute bottom-6 left-6 max-w-md font-serif text-xl italic text-ivory sm:bottom-10 sm:left-10 sm:text-2xl">
            A good room is built, not found.
          </p>
        </div>
      </FadeUp>
      <FadeUp delay={0.1}>
        <div className="mt-12 grid gap-10 md:grid-cols-2">
          <p className="text-lg leading-relaxed text-ivory/75">
            Instead, we aim to create a good room: a manageable number of men, a balance of
            preferences and personalities, and people who understand that a great night depends on
            how everyone treats everyone else.
          </p>
          <p className="text-lg leading-relaxed text-ivory/75">
            That's why we ask a few questions when you join the Players List. They help us create
            gatherings where there is enough variety to make things interesting — and enough shared
            understanding to make everyone feel comfortable.
          </p>
        </div>
      </FadeUp>
    </section>

    <section className="bg-ivory text-ink">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <FadeUp>
          <p className="text-[11px] uppercase tracking-[0.3em] text-burgundy">
            <span aria-hidden="true" className="mr-2">♥</span>Who's in the Lounge?
          </p>
          <h2 className="mt-4 max-w-3xl font-serif text-3xl leading-tight sm:text-5xl">
            Players Lounge welcomes men aged 18 and over.
          </h2>
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-ink/80">
            Older men. Younger men. Bigger bodies. Leaner bodies. Hairy bodies. Smooth bodies.
            Confident extroverts and slightly nervous first-timers. Tops, bottoms, versatile guys
            and men who don't particularly care for labels.
          </p>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink/80">
            What matters isn't fitting a particular image. What matters is knowing how to share a
            room: respectful, friendly, discreet, comfortable hearing "no", and generous enough to
            help create a good night for everyone else as well as yourself.
          </p>
          <p className="mt-7 font-serif text-2xl italic text-burgundy">
            If that sounds like you, there's probably a seat at the table.
          </p>
        </FadeUp>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
      <FadeUp>
        <Eyebrow>The code of the Lounge</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl text-ivory sm:text-5xl">The four suits</h2>
      </FadeUp>
      <div className="mt-12 space-y-5">
        {SUITS.map((s, i) => (
          <FadeUp key={s.title} delay={i * 0.06}>
            <article
              data-testid={`suit-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex flex-col gap-4 rounded-2xl border border-brass/15 bg-charcoal/50 p-8 transition-all duration-300 hover:border-brass/40 sm:flex-row sm:items-center sm:gap-10 sm:p-10"
            >
              <span
                aria-hidden="true"
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-brass/25 text-2xl text-brass transition-colors duration-300 group-hover:bg-brass/10"
              >
                {s.suit}
              </span>
              <div>
                <h3 className="font-serif text-2xl text-ivory sm:text-3xl">{s.title}</h3>
                <p className="mt-2 max-w-2xl text-base leading-relaxed text-ivory/70">{s.body}</p>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.15}>
        <Link
          to="/house-rules"
          data-testid="players-next-rules-link"
          className="group mt-12 inline-flex items-center gap-3 rounded-xl border border-brass/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-brass transition-all duration-200 hover:border-brass hover:bg-brass/10"
        >
          Read the House Rules
          <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </section>
  </div>
);

export default ThePlayers;
